import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import type { CombatPowerResponse, MonthlyCombatPower } from '../../../types/combat-power.js';
import { aggregateMembers, BudgetBarChart } from './BudgetBarChart.js';
import type { ChartItem } from './BudgetBarChart.js';
import { calcAwards, AwardBlock } from './Awards.js';

/** @graph-connects none */
export function TeamQuarterChart({
  data,
  teamLabel,
  members,
  groupContext,
  currentMonthIdx,
}: {
  data: CombatPowerResponse;
  teamLabel: string;
  members: MonthlyCombatPower[];
  groupContext?: string;
  currentMonthIdx: number;
}) {
  const { t } = useTranslation();

  const chartData: ChartItem[] = data.monthHeaders.map((month, idx) => {
    const agg = aggregateMembers(members, idx);
    return {
      label: month,
      actual: agg.actual,
      actualInProgress: agg.actualInProgress,
      target: agg.capacity,
      currentTarget: agg.target,
      teamContext: groupContext,
    };
  });

  const { topContributors, risingStars } = calcAwards(members, currentMonthIdx);

  // 過去 Q 選択時に「当Q」表記だと誤りになるため、選択中 Q 名 (「まとめ」suffix は除去) をタイトルに出す
  const quarterLabel = data.selectedSheet.replace(/まとめ$/, '');

  return (
    <Card className="animate-content-reveal">
      <CardHeader
        title={`${teamLabel} — ${quarterLabel} ${t('members.combatPower.quarterChart')}`}
      />
      <CardContent>
        <div className="flex gap-4">
          <div className="flex-1 h-56">
            <BudgetBarChart items={chartData} height={224} barSize={40} showXAxis showRate />
          </div>
          <div className="flex flex-col gap-3 shrink-0">
            <AwardBlock
              title={t('members.combatPower.topContributor')}
              items={topContributors}
              crownColor="#eab308"
            />
            <AwardBlock
              title={t('members.combatPower.risingStar')}
              items={risingStars}
              crownColor="#94a3b8"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
